import { isNil, pick } from 'lodash';
import { useRef } from 'react';
import Beautify from 'ace-builds/src-noconflict/ext-beautify';
import { ACE_EDITOR_DEFAULT_OPTIONS, ACE_EDITOR_DEFAULT_PROPS } from './ace-editor-defaults';

const PADDING_TOP = 21; // ~1.5rem
const PADDING_BOTTOM = 21; // ~1.5rem

// throttle onChange events
// ace editor pastes will trigger ~ four onChange events
// and it causes reactivity issues with react-hook-form formStatus.isDirty
const ONCHANGE_PASTE_DEBOUNCE_MS = 50;

export const DEFAULT_PROPS = {
  ...ACE_EDITOR_DEFAULT_PROPS,
  debounceChangePeriod: ONCHANGE_PASTE_DEBOUNCE_MS,
  commands: [...Beautify.commands],
};

// needed when using the editor within a component-library use-form hook
// it assumes the dataset attribute exists and tries to destructure it
const EMPTY_DATASET = {};

export default function useAceEditorProps(props) {
  const editorRef = useRef();

  return {
    ...DEFAULT_PROPS,
    ...buildClassName(props),
    ...buildMode(props),
    ...buildPassThroughProps(props),
    ...buildLines(props),
    ...buildOptions(props),
    ...buildOnChange(props),
    ...buildOnBlur(props),
    ...buildOnInput({ props, editorRef }),
    ...buildOnLoad({ props, editorRef }),
  };
}

// adds additional sass styles if in readOnly mode
function buildClassName({ readOnly }) {
  if (readOnly === true) {
    return {
      className: `${DEFAULT_PROPS.className} read-only`,
    };
  }

  return {
    className: DEFAULT_PROPS.className,
  };
}

function buildMode({ lang }) {
  if (isNil(lang)) {
    return;
  }

  return {
    mode: lang,
  };
}

const PASS_THROUGH_PROPS = ['onFocus', 'defaultValue', 'value', 'readOnly', 'name', 'placeholder'];

function buildPassThroughProps(props) {
  return pick(props, PASS_THROUGH_PROPS);
}

/**
 * Since we are setting setScrollMargin in buildOnLoad, ace is adding one
 * extra code row of height due to the extra scroll height.
 *
 * EX: rows: 20 = ace editor shows 21 lines.
 *
 * This removes the extra code line so rows: 20 = ace editor shows 20 lines.
 */
function buildLines({ rows }) {
  if (isNil(rows)) {
    return;
  }

  const extraMarginRows = 1;
  const adjustedRows = rows > 1 ? rows - extraMarginRows : 1;

  return {
    maxLines: adjustedRows,
  };
}

export const DEFAULT_OPTIONS = ACE_EDITOR_DEFAULT_OPTIONS;

function buildOptions({ options, foldable }) {
  const foldOptions = foldable ? { showFoldWidgets: true, foldStyle: 'markbegin' } : {};

  return {
    setOptions: {
      ...DEFAULT_OPTIONS,
      ...foldOptions,
      ...options,
    },
  };
}

// ND: The AceEditor passes the value back
// but for backwards compatability I'm going to pass the event
// instead. In the future if we're just using Ace we can refactor this.
function buildOnChange({ id, name, onChange }) {
  if (isNil(onChange)) {
    return;
  }
  return {
    onChange: value => {
      onChange({ target: { id, name, value, dataset: EMPTY_DATASET } });
    },
  };
}

// see Nick's comment above for backwards compatability, applies here too
function buildOnBlur({ id, name, onBlur }) {
  if (isNil(onBlur)) {
    return;
  }
  return {
    // target.value is empty on the event param, use editor to get the value
    onBlur: (event, editor) => {
      const value = editor.getValue();
      onBlur({ target: { id, name, value, dataset: EMPTY_DATASET } });
    },
  };
}

/**
 * The default ace editor onInput event returns an empty object.  Mirroring
 * event signature as other on* events.
 */
function buildOnInput({ props, editorRef }) {
  const { id, name, onInput } = props;

  if (isNil(onInput)) {
    return;
  }

  return {
    onInput: () => {
      if (isNil(editorRef.current)) {
        return;
      }

      const value = editorRef.current.getValue();

      onInput({ target: { id, name, value, dataset: EMPTY_DATASET } });
    },
  };
}

/**
 * React-ace's onPaste event fires _before_ the underlying ace editor actually
 * finishes handles the paste event.
 *
 * This leaves us with a weird timing issue to attempt beautifying
 * the pasted code _after_ ace editor has fully handled the paste event.
 *
 * Instead of useEffect or setTimeout via onPaste, tapping into ace editor's
 * afterExec event, which will fire _after_ all events.
 *
 * @see buildOnLoad
 */
function buildBeautifyAfterExecPaste({ props, editorRef }) {
  return event => {
    if (isNil(editorRef.current)) {
      return;
    }

    if (isNil(event?.command) || event.command.name !== 'paste') {
      return;
    }

    // only doing json, this will not work for lua or yaml
    if (props.lang === 'json') {
      beautifyJson(editorRef);
    }
  };
}

function beautifyJson(editorRef) {
  try {
    // if valid JSON, format consistently with other code editors
    const currentText = editorRef.current.getValue();
    const parsed = JSON.parse(currentText);
    const formatted = JSON.stringify(parsed, null, 2);
    editorRef.current.setValue(formatted);

    // after setValue, stop the editor from highlighting the entire document
    editorRef.current.clearSelection();
  } catch (e) {
    // docs say the beautify ext is legacy and should only be used for HTML, CSS, JavaScript, and PHP
    // it will work on invalid JSON, but formats JSON arrays oddly
    editorRef.current.execCommand('beautify');
  }
}

/**
 * BB: I tried all sorts of css options for top / bottom padding.
 *  Ace editor sets a dynamic transform: translate on .ace_content that overrides it.
 *  Many GH issues refer to using setScrollMargin directly for top / bottom padding
 *  and these values get added to .ace_content transform: translate.
 *
 * EXAMPLE
 * <div class="ace_content" style="transform: translate(0px, 21px); width: 537.799px; height: 758px;">
 *
 * @see https://github.com/securingsincity/react-ace/issues/386
 * @see https://ajaxorg.github.io/ace-api-docs/classes/ace.VirtualRenderer.html#setScrollMargin
 */
function buildOnLoad({ props, editorRef }) {
  return {
    onLoad: editor => {
      if (isNil(editor)) {
        return;
      }

      editorRef.current = editor;

      // must be a number in px for ace editor
      editorRef.current.renderer.setScrollMargin(PADDING_TOP, PADDING_BOTTOM);

      // handle _after_ paste formatting
      const beautifyAfterExecPaste = buildBeautifyAfterExecPaste({ props, editorRef });
      editorRef.current.commands.on('afterExec', beautifyAfterExecPaste);

      // Pass the live ace editor instance to a caller-supplied hook
      // so consumers can register custom completers, markers, key
      // bindings, etc. — anything that needs the underlying editor
      // ref. Runs after the internal setup above so caller logic
      // sees the fully-initialised editor.
      props.onEditorMount?.(editor);
    },
  };
}
